/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'

import type { AdminAssetGroup } from '../types'

interface Props {
  groups: readonly AdminAssetGroup[]
  /** Limits the options to this company's groups; undefined lists every group. */
  customerId?: number
  value?: number
  onChange: (companyId?: number) => void
}

/**
 * Single-select over asset groups for the user list's filter (MEGA-776). With a company
 * picked it offers only that company's groups; without one, every group, labelled with
 * its company so same-named depots stay apart.
 */
export const AssetGroupFilter: React.FC<Props> = ({ groups, customerId, value, onChange }) => {
  const options: DropdownOption[] = groups
    .filter((group) => customerId === undefined || group.customerId === customerId)
    .map((group) => ({
      label: customerId === undefined ? `${group.name} (${group.customerName})` : group.name,
      value: String(group.companyId),
    }))
  const selectedItem = options.find((o) => Number(o.value) === value) ?? { label: '', value: '' }

  return (
    <div data-testid="asset-group-filter">
      <label>Asset group</label>
      <Dropdown
        options={options}
        selectedItem={selectedItem}
        handleSingleSelectItem={(option: DropdownOption | null) =>
          onChange(option && option.value ? Number(option.value) : undefined)
        }
        placeholder={options.length === 0 ? 'No asset groups' : 'Select asset group...'}
      />
    </div>
  )
}

export default AssetGroupFilter
